import { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Tile } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { selectedEstablishmentIdsAtom } from 'atoms/establishments-atoms';
import { TitleChainValidatorProps } from './types';
import { TitleChainValidatorForm } from './title-chain-validator-form';
import { TitleChainValidatorError } from './title-chain-validator-error';
import { useTitleValidation } from 'lib/hooks/useTitleValidation';

export const TitleChainValidator = ({
  titleValidatorUrl,
  documentId,
  entities,
  entitiesLoading,
}: TitleChainValidatorProps) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [targetEntityId, setTargetEntityId] = useState(
    () => searchParams.get('target_entity_id') || ''
  );
  const [coverageThreshold, setCoverageThreshold] = useState(
    () => searchParams.get('coverage_threshold') || '1'
  );
  const [cutoffDate, setCutoffDate] = useState(
    () => searchParams.get('cutoff_date') || ''
  );
  const selectedEstablishmentIds = useAtomValue(selectedEstablishmentIdsAtom);
  const { loading, error, progress, timestamp } = useTitleValidation(
    titleValidatorUrl,
    documentId
  );

  // Validation runs on the standalone validator page; this panel collects the
  // form state and redirects there.
  const handleSubmit = () => {
    const params = new URLSearchParams({
      target_entity_id: targetEntityId,
      coverage_threshold: coverageThreshold,
    });
    if (cutoffDate) {
      params.set('cutoff_date', cutoffDate);
    }
    if (selectedEstablishmentIds?.length) {
      selectedEstablishmentIds.forEach((id) =>
        params.append('establishment_ids[]', String(id))
      );
    }
    history.push(
      `/documents/${documentId}/title_validators?${params.toString()}`
    );
  };

  const entitiesArray = entities && !('error' in entities) ? entities : [];

  return (
    <div style={{ maxWidth: '100%' }} data-testid="title-chain-validator">
      <Tile>
        <TitleChainValidatorForm
          targetEntityId={targetEntityId}
          coverageThreshold={coverageThreshold}
          cutoffDate={cutoffDate}
          isLoading={loading}
          entities={entitiesArray}
          entitiesLoading={entitiesLoading}
          progress={progress}
          hasResult={false}
          hasError={!!error}
          validationTimestamp={timestamp}
          documentId={documentId}
          onTargetEntityIdChange={setTargetEntityId}
          onCoverageThresholdChange={setCoverageThreshold}
          onCutoffDateChange={setCutoffDate}
          onSubmit={handleSubmit}
        />
      </Tile>

      {error && <TitleChainValidatorError error={error} />}
    </div>
  );
};
